import * as R from 'ramda';
import { RaterApprovalParticipant, RaterApprovalRater } from './models';
import { RaterApprovalStatus } from './raterApprovalStatus';


/**
 * The collection of all rater statuses which can be edited by the approver in the UI
 */
const editableStatuses = [
  RaterApprovalStatus.None, RaterApprovalStatus.NeedApproval, RaterApprovalStatus.WaitingForApproval,
];


/**
 * Returns the participant with the provided `projectParticipant` prop (or undefined if none)
 *
 * @param projectParticipantKey - the value of the maybe participant's `projectParticipant` prop
 * @param participants - the list of participants to search
 */
export const participantByUniqueIdentifier = (
  projectParticipantKey: string,
  participants: RaterApprovalParticipant[],
) => participants.find(p => p.projectParticipant === projectParticipantKey);


/**
 * Returns the rater with the provided `email` prop (or undefined if none)
 *
 * @param email - the value of the maybe rater's `email` prop
 * @param raters - the list of raters to search
 */
export const raterByEmailAddress = (
  email: string,
  raters: RaterApprovalRater[],
) => raters.find(rater => rater.email === email);


/**
 * Returns all raters from the provided collection, whose current approval status matches the one provided
 *
 * @param approvalStatus - the approval status for which to retrieve all matching raters
 * @param raters - the list of raters to search
 */
export const ratersByApprovalStatus = (
  approvalStatus: number,
  raters?: RaterApprovalRater[],
) => raters?.filter(rater => rater.approvalStatus === approvalStatus);


/**
 * Returns all raters from the provided collection, whose current approval status matches one of the values in the list
 * provided
 *
 * @param approvalStatuses - the collection of approval statuses for which to retrieve all matching raters
 * @param raters - the list of raters to search
 */
export const ratersByApprovalStatuses = (
  approvalStatuses: RaterApprovalStatus[],
  raters?: RaterApprovalRater[],
) => raters?.filter(rater => R.contains(rater.approvalStatus, approvalStatuses));


/**
 * Use this to check whether the provided rater's approval status matches the one provided
 *
 * @param approvalStatus - the approval status to check for
 * @param approvable - the rater (or other object with an `approvalStatus` prop) whose 'editability' we wish to check
 */
export const hasStatus = (
  approvalStatus: RaterApprovalStatus,
  approvable: { approvalStatus: number },
) => approvable.approvalStatus === approvalStatus;

/**
 * Use this to check whether the provided rater's approval status matches any of the ones provided
 *
 * @param approvalStatuses - collection of approval statuses to check against
 * @param approvable - the rater (or other object with an `approvalStatus` prop) whose 'editability' we wish to check
 */
export const hasStatusIn = (
  approvalStatuses: RaterApprovalStatus[],
  approvable: { approvalStatus: number },
) => R.indexOf(approvable.approvalStatus, approvalStatuses) > -1;


/**
 * Use this to determine whether a rater approver has permission to edit the rater's approver status
 *
 * @param approvable - the rater (or other object with an `approvalStatus` prop) whose 'editability' we wish to check
 * @returns false if the status indicates that prior approval or denial has already been decided, otherwise true
 */
export const canEditRater = (
  approvable: { approvalStatus: number },
) => hasStatusIn(editableStatuses, approvable);


/**
 * Use this to check whether the current rater has been previously approved
 *
 * @param approvable - the rater (or other object with an `approvalStatus` prop) whose 'editability' we wish to check
 */
export const isApproved = (
  approvable: { approvalStatus: number },
) => hasStatus(RaterApprovalStatus.Approved, approvable);


/**
 * Use this to check whether the current rater has been previously declined
 *
 * @param approvable - the rater (or other object with an `approvalStatus` prop) whose 'editability' we wish to check
 */
export const isDeclined = (
  approvable: { approvalStatus: number },
) => hasStatus(RaterApprovalStatus.Declined, approvable);


/**
 * Use this to return the database sort value associated with the provided rater
 *
 * This is used for a few niche applications, like setting a mapping key with this value as a coefficient to maintain
 * the default sort when extracting raters back into an array
 *
 * @param approvable - the rater (or other object with an `approvalStatus` prop) whose 'editability' we wish to check
 */
export const sortValueFor = (
  approvable: { approvalStatus: number },
) => R.indexOf(approvable.approvalStatus, editableStatuses) > -1 ? 1 : 2;
